/**
 * OAuth2 consent page — shown when an OAuth/MCP client (e.g. Claude) requests
 * access and the authorization server routes the user here instead of
 * auto-accepting (see the acceptConsentRequest override in apps/api). Port of
 * shiftops-web's consent page.
 *
 * Reads `consentChallenge` from the URL, fetches the client + requested scopes,
 * and renders Allow/Deny. The decision is submitted to the API, which returns
 * the URL to continue the OAuth flow; we follow it with a full-document
 * navigation so the browser completes SuperTokens' redirect chain to the client.
 */
import { useEffect, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { captureException } from '@sentry/react';
import { Button } from '@sara/ui/components/button';
import { Logo } from '@sara/ui/components/logo';
import { env } from '../../config/env';

// Friendly labels for the scopes we issue; unknown scopes fall back to the raw
// value so a new scope still renders something sensible.
const SCOPE_LABELS: Record<string, string> = {
  openid: 'Verify your identity',
  offline_access: 'Stay connected when you’re away from the app',
  'org:read':
    'Read your organizations’ data — interviews and transcripts, candidates and their contact ' +
    'details, interview templates, extractions, and rubrics — and create interviews for ' +
    'candidates, including placing an outbound phone call to a number it supplies',
};

interface ConsentInfo {
  clientName: string;
  scopes: string[];
  redirectHosts: string[];
}

type Status = 'loading' | 'ready' | 'submitting' | 'error';

async function fetchConsentInfo(challenge: string): Promise<ConsentInfo> {
  const url = new URL('/oauth-consent/info', env.VITE_API_DOMAIN);
  url.searchParams.set('consentChallenge', challenge);
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`consent info failed: ${response.status}`);
  return response.json() as Promise<ConsentInfo>;
}

class NotAuthenticatedError extends Error {}

async function submitConsent(challenge: string, decision: 'accept' | 'reject'): Promise<string> {
  const url = new URL(`/oauth-consent/${decision}`, env.VITE_API_DOMAIN);
  const response = await fetch(url, {
    method: 'POST',
    // Cookie-authenticated: the session identifies the user granting consent.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ consentChallenge: challenge }),
  });
  // No session (expired between login and the click): accept/reject 401. Signal
  // it distinctly so the page can offer a sign-in path instead of a dead retry.
  if (response.status === 401) throw new NotAuthenticatedError();
  if (!response.ok) throw new Error(`consent ${decision} failed: ${response.status}`);
  const body = (await response.json()) as { redirectTo: string };
  return body.redirectTo;
}

export function ConsentPage() {
  const search = useSearch({ strict: false }) as { consentChallenge?: string };
  const consentChallenge = search?.consentChallenge;

  // The missing-challenge case is knowable from the URL alone, so it's reflected in the
  // initial state directly rather than set from the effect below.
  const [status, setStatus] = useState<Status>(() => (consentChallenge ? 'loading' : 'error'));
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [error, setError] = useState<string | null>(() =>
    consentChallenge ? null : 'This authorization request is missing its consent challenge.'
  );
  const [needsSignIn, setNeedsSignIn] = useState(false);

  useEffect(() => {
    if (!consentChallenge) return;
    let cancelled = false;
    fetchConsentInfo(consentChallenge)
      .then((result) => {
        if (!cancelled) {
          setInfo(result);
          setStatus('ready');
        }
      })
      .catch(() => {
        // Expected: the API rejects expired/unknown challenges. User-facing
        // error only, no Sentry noise.
        if (!cancelled) {
          setError('This authorization request is invalid or has expired.');
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [consentChallenge]);

  const decide = async (decision: 'accept' | 'reject') => {
    if (!consentChallenge) return;
    setStatus('submitting');
    setError(null);
    try {
      const href = await submitConsent(consentChallenge, decision);
      // `href` is an absolute, cross-origin API URL (the SuperTokens redirect
      // chain), never an in-app route — use a plain document navigation so the
      // origin is preserved. TanStack's navigate() would resolve it as a
      // relative path and land back on the console origin.
      window.location.assign(href);
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        // No session — the two buttons can't work; offer sign-in instead of a
        // dead retry. Signing in returns here (the challenge stays in the URL).
        setNeedsSignIn(true);
        setError('Your session has expired. Please sign in, then continue.');
        setStatus('ready');
        return;
      }
      captureException(err);
      setError('Something went wrong submitting your choice. Please try again.');
      setStatus('ready');
    }
  };

  const signInThenReturn = () => {
    // SuperTokens' prebuilt UI reads `redirectToPath` (not `returnTo`) to return
    // the user after login — so send them to sign in and back to this exact
    // consent URL (challenge preserved) to resume the flow.
    const back = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/auth?redirectToPath=${encodeURIComponent(back)}`);
  };

  const busy = status === 'submitting';

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-md space-y-8 px-6 py-16">
        <div className="flex flex-col items-center gap-6 text-center">
          <Logo className="h-14 w-14" />

          {status === 'error' ? (
            <div className="space-y-3">
              <h1 className="font-display text-3xl font-medium tracking-tight text-foreground">
                Authorization unavailable
              </h1>
              <p className="text-[15px] leading-relaxed text-muted-foreground">{error}</p>
            </div>
          ) : status === 'loading' || !info ? (
            <p className="text-[15px] text-muted-foreground">Loading authorization request…</p>
          ) : (
            <div className="w-full space-y-8">
              <div className="space-y-3">
                <h1 className="font-display text-3xl font-medium tracking-tight text-foreground">
                  Allow access?
                </h1>
                <p className="text-[15px] leading-relaxed text-muted-foreground">
                  <span className="font-medium text-foreground">{info.clientName}</span> wants to
                  access your AI Interviewer account.
                </p>
                {info.redirectHosts.length > 0 && (
                  <div className="space-y-1 text-[13px] leading-relaxed text-muted-foreground">
                    <p>
                      After you allow, you’ll be sent to{' '}
                      {info.redirectHosts.length > 1 ? 'one of these' : 'this destination'}. Only
                      continue if you recognize {info.redirectHosts.length > 1 ? 'them all' : 'it'}:
                    </p>
                    {/* Bounded box: every host is shown (none hidden), but a client
                        with many/long hosts can't push the buttons off-screen. */}
                    <div className="max-h-24 overflow-y-auto rounded-md border border-border p-2">
                      {info.redirectHosts.map((host) => (
                        <p key={host} className="break-all font-medium text-foreground">
                          {host}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {info.scopes.length > 0 && (
                <div className="space-y-2 text-left">
                  <p className="text-sm font-medium text-foreground">This will let it:</p>
                  <ul className="space-y-1.5">
                    {info.scopes.map((scope) => (
                      <li
                        key={scope}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <span aria-hidden className="mt-0.5 text-primary">
                          •
                        </span>
                        {SCOPE_LABELS[scope] ?? scope}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}

              {needsSignIn ? (
                <div className="flex flex-col gap-2.5">
                  <Button type="button" onClick={signInThenReturn}>
                    Sign in
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  <Button type="button" onClick={() => decide('accept')} disabled={busy}>
                    {busy ? 'Working…' : 'Allow'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => decide('reject')}
                    disabled={busy}
                  >
                    Deny
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
